import { FC, useState, useEffect, useMemo } from 'react';
import { api, useApi } from '../api/api';
import { Button, Container, Dimmer, Loader, Segment, Accordion, Icon, Grid, Label, SemanticCOLORS, Message, Dropdown, DropdownMenu, DropdownItem, DropdownDivider, DropdownHeader, Tab } from 'semantic-ui-react';
import Table, { TextCell, Cell, ActionsCell } from '../components/Table';
import { HealthCondition, ScheduleRule } from '../api/health_monitor';
import { SyncButton } from '../components/buttons';
import { envColor } from '../common';


const severitySettings: { label: string, color: SemanticCOLORS }[] = [
    {label: "Clear", color: "green" },
    {label: "Harmless", color: "olive" },
    {label: "Warning", color: "yellow"},
    {label: "Error", color: "orange" },
    {label: "Urgent", color: "red" }
];


const DEFAULT_BASE_LEVEL = severitySettings.findIndex(item => item.label == "Warning");


const SeverityFilter: FC<{ level: number, onChange(_: number): void }> = ({ level, onChange }) => (
    <Dropdown
        button
        icon='filter'
        floating
        className='icon'
        labeled
        text={`Base Severity : ${severitySettings[level].label}`}
        options={severitySettings.map((item, index) => ({
            key: item.label,
            text: item.label,
            value: index,
            label: { color: item.color, circular: true, empty: true }
        }))}
        value={level}
        onChange={(_, { value }) => {
            if (typeof value === "number") {
                onChange(value);
            }
        }}
    />
);


const RulesToggle:  FC<{ enabled: boolean, onChange(): void }> = ({ enabled, onChange }) => (
    <Button
        icon={enabled ? "toggle on" : "toggle off"}
        content={(enabled ? "Hide" : "Show") + " Rules"}
        onClick={() => { onChange(); }}
        labelPosition='left'
    />
);


const ConditionsView: FC<{ conditions: HealthCondition[], showRules: boolean }> = ({ conditions, showRules }) => {
    return (
        <Table rows={conditions}>
            <TextCell header='Name' value={r => r.name} />
            <TextCell header='Severity' value={r => severitySettings[r.severity + 1].label} />
            <TextCell header='Trigger' value={r => r.check_trigger} />
            <TextCell header='Last Check' value={r => r.last_check.substr(0, r.last_check.indexOf("."))} />
            {showRules ? <TextCell header='Rule' value={r => r.rule} /> : null}
            <TextCell header='Is Active' value={r => r.is_active.toString()} />
        </Table>
    );
};


const getMaxSeverity = (conditions: HealthCondition[]) => {
    let severity = 0;

    conditions
        .filter(c => c.is_active)
        .forEach(c => {
            if (typeof c.severity === "number") {
                severity = Math.max(severity, c.severity + 1);
            }
        });

    return severity;
};


const VenueStatus: FC<{
    source: string,
    conditions: HealthCondition[],
    isOpen: boolean,
    showRules: boolean,
    toggle: () => void
}> = ({ source, conditions, isOpen, showRules, toggle }) => {
    let severity = getMaxSeverity(conditions);
    let totalIssues = conditions.filter(c => c.is_active).length;

    const status = severitySettings[severity];

    return (
        <Accordion key={source} index={source} active={isOpen ? true : undefined} styled>
            <Accordion.Title onClick={toggle}>
                <Container>
                    <p style={{fontSize: "20px", textAlign: "center"}}>{source}</p>
                    <div style={{fontSize: "16px", textAlign: "center", margin: "10px" }}>
                        <p style={{display: "inline"}}>Status : </p>
                        <Label size='large' basic color={status.color}>{status.label}</Label>
                        {(severity > 0) ?
                            <p style={{display: "block", marginTop: "5px"}}>Total issues : {totalIssues}</p>
                            : false
                        }
                    </div>
                    <div style={{"textAlign": 'center'}}>
                        <Label>
                            <Icon style={{display: "inline"}} name={isOpen ? "caret down" : "caret right"}></Icon>
                            {isOpen ? "Hide" : "Conditions"}
                        </Label>
                    </div>
                </Container>
            </Accordion.Title>
            <Accordion.Content active={isOpen}>
                <ConditionsView conditions={conditions} showRules={showRules} />
            </Accordion.Content>
        </Accordion>
    );
};


const MonitorResults: FC<{}> = () => {
    const healthMonitor = useApi(api.healthMonitor);
    const [loading, setLoading] = useState(false);
    const [activeIndex, setActiveIndex] = useState<Set<string>>(new Set<string>());
    const [sources, setSources] = useState<Record<string, HealthCondition[]>>({});
    const [baseSeverity, setBaseSeverity] = useState<number>(DEFAULT_BASE_LEVEL);
    const [log, setLog] = useState(null);
    const [showRules, setShowRules] = useState(false);

    const toggleKey = (key: string) => {
        const newIndex = new Set(activeIndex);

        newIndex.has(key) ?
            newIndex.delete(key) : newIndex.add(key);

        setActiveIndex(newIndex);
    };

    const toggleRules = () => {
        setShowRules(!showRules);
    };

    const load = async (useLoadMsg: boolean) => {
        if (useLoadMsg) {
            setLoading(true);
        }

        setLog(null);
        const conditions = await healthMonitor.index();

        let sources: Record<string, HealthCondition[]> = {};
        conditions.forEach(x => {
            if (x.source === undefined) return;

            const source = x.source.split("/").slice(2).join("/");
            if (!(source in sources)) {
                sources[source] = [];
            }

            sources[source].push(x);
        });

        setSources(sources);
        if (useLoadMsg) {
            setLoading(false);
        }
    };

    useEffect(() => {
        load(true).catch(console.error);

        const interval = setInterval(() => {
            console.log("Auto-refresh");
            load(false).catch(console.error);
        }, 1000 * 30);

        return () => clearInterval(interval);
    }, []);

    const renderNothing = () => (
        <Segment style={{ marginTop: "10px" }}>
            <div style={{ fontSize: "20px", textAlign: "center" }}>
                No conditions to display
            </div>
        </Segment>
    );

    const sourceKeys =
        Object.keys(sources)
        .filter(source => getMaxSeverity(sources[source]) >= baseSeverity);


    return (
        <Container>
            <Segment>
                <SyncButton load={load} color={envColor} />
                <SeverityFilter level={baseSeverity} onChange={setBaseSeverity} />
                <RulesToggle enabled={showRules} onChange={() => toggleRules()} />

                {log ? (
                    <Message info>
                        <p>{JSON.stringify(log, null, 2)}</p>
                    </Message>
                ) : null}

                {loading ?
                    <Dimmer inverted active><Loader/></Dimmer> :
                        <Accordion fluid styled style={{ boxShadow: "0px 0px 0px 0px" }}>
                            {sourceKeys.length > 0 ?
                                sourceKeys.map(source => (
                                    <VenueStatus
                                        key={source}
                                        source={source}
                                        conditions={sources[source]}
                                        isOpen={activeIndex.has(source)}
                                        toggle={() => toggleKey(source)}
                                        showRules={showRules}
                                    />
                                ))
                                : renderNothing()
                            }
                        </Accordion>
                }
            </Segment>
        </Container>
    );
}


const Schedule = () => {
    const healthMonitor = useApi(api.healthMonitor);
    const [loading, setLoading] = useState(false);
    const [rules, setRules] = useState<ScheduleRule[]>([]);

    const load = async () => {
        setLoading(true);

        const newRules = await healthMonitor.listRules();
        setRules(newRules);

        setLoading(false);
    };

    useEffect(() => {
        load().catch(console.error);
    }, []);

    if (loading) {
        return <Dimmer inverted active><Loader/></Dimmer>;
    }

    if (rules.length == 0) {
        return <Segment>Health check schedule is empty</Segment>
    }

    const actions = [
        {icon: "toggle on", onClick: async (rule: ScheduleRule) => {
            await healthMonitor.toggleRule(rule);
            await load();
        }}
    ];

    return (
        <Container>
            <Segment>
                <Table rows={rules}>
                    <ActionsCell header='' actions={actions} />
                    <TextCell header='Expression' value={r => r.expression} />
                    <TextCell header='Is Enabled' value={r => r.is_enabled.toString()} />
                </Table>
            </Segment>
        </Container>
    );
};


const panes = [
    {menuItem: "Status", render: () => <MonitorResults />},
    {menuItem: "Schedule", render: () => <Schedule />}
];


const HealthMonitor = () => (
    <Container>
        <Tab panes={panes} />
    </Container>
);


export default HealthMonitor;
